/**
 * EmailComposePanel — floating composer for drafting a NEW email.
 *
 * Peer of MemoComposePanel: shares the floating ComposeShell chrome but renders
 * the email header fields + body (via the existing EmailEditor) with NO
 * associated records. On Send it routes through the gmail_send action pipeline
 * (see useSendComposedEmail) — the same provider-agnostic path (Gmail/Outlook)
 * the AI flow uses.
 *
 * The AI chat at the bottom is powered by the proposal-discussion agent (see
 * useComposeProposalChat): the draft lives on a pending gmail_send proposal
 * and each chat turn is a discuss round with the same agent AI Actions uses.
 *
 * EmailComposeRenderer is mounted once in Root.tsx; it owns org/user resolution
 * and portals the panel onto document.body.
 */

import { XClose } from '@untitledui/icons';
import type { FormEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks';
import EmailEditor, {
  type EmailEditorHandle,
} from '@/components/emails/EmailEditor';
import { EmailPreviewModal } from '@/components/emails/preview/EmailPreviewModal';
import type { EmailPreviewPayload } from '@/components/emails/preview/useEmailPreview';
import type { EmailComposeConfig } from '@/contexts/EmailComposeContext';
import {
  useEmailCompose,
  useEmailComposeState,
} from '@/contexts/EmailComposeContext';
import { useOrg } from '@/contexts/OrgContext';
import {
  selectSignature,
  useAccountSignatures,
} from '@/hooks/email/useAccountSignatures';
import { useConnectedAccounts } from '@/hooks/integrations/useConnectedAccounts';
import { Button } from '@/lib/base/buttons/button';
import type { FieldConfig } from '@/lib/base/forms';
import { FormProvider, SubmitButton } from '@/lib/base/forms';
import { ConfirmationDialog } from '@/lib/base/overlays/confirmation-dialog';
import { ChatResponsePanel } from '@/lib/chat/ChatResponsePanel';
import { ComposeShell } from '@/lib/compose/ComposeShell';
import type { ComposeChatMessage } from '@/lib/compose/useComposeProposalChat';
import { useComposeProposalChat } from '@/lib/compose/useComposeProposalChat';
import { useSendComposedEmail } from '@/lib/compose/useSendComposedEmail';
import { filterFilesByAttachmentCap } from '@/lib/files/attachmentValidation';
import {
  MAX_EMAIL_ATTACHMENT_TOTAL_BYTES,
  MAX_EMAIL_ATTACHMENT_TOTAL_MB,
} from '@/lib/files/constants';
import type { UploadedFile } from '@/lib/files/service';
import { fileService } from '@/lib/files/service';
import type { EmailContent } from '@/lib/proposals/core/emailContent';
import type { SignatureDisposition } from '@/lib/proposals/utils/signatureDisposition';
import {
  appendSignatureBlock,
  reconcileSignatureDisposition,
  wrapSignatureBlock,
} from '@/lib/proposals/utils/signatureDisposition';
import {
  SIGNATURE_BLOCK_MARKER_ATTR,
  SIGNATURE_DELIMITER_HTML,
} from '@/lib/reasoning/components/email-editor/constants';
import { htmlToPlainText } from '@/lib/reasoning/components/email-editor/plateEmailSerializer';
import { ConversationInput } from '@/lib/reasoning/ConversationInput';
import { cx } from '@/utils/cx';

// ---------------------------------------------------------------------------
// Compose content state
// ---------------------------------------------------------------------------

interface ComposeContent {
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
  body_html?: string;
  social_account_id?: number;
  attachment_file_ids: string[];
  // The draft's typed signature state, kept explicit so it survives AI
  // round-trips and threads into send AND preview identically. 'inserted' is
  // set when a marker block is seeded (or arrives in a prefill); any edit that
  // drops the marker flips it to 'user_removed' via
  // reconcileSignatureDisposition in handleContentChange.
  signature_disposition: SignatureDisposition;
}

// The gmail_send params treat cc/bcc as optional: send an array only when it has
// entries, otherwise `undefined` (which JSON.stringify drops). Exported so
// EmailActionPreview.tsx shares this exact normalization instead of a second copy.
// eslint-disable-next-line react-refresh/only-export-components -- shared pure helper co-located with the component that owns it, not a component itself
export function optionalRecipients(
  list: string[] | undefined
): string[] | undefined {
  return list && list.length > 0 ? list : undefined;
}

// eslint-disable-next-line react-refresh/only-export-components -- re-exported for existing importers; defined in ./email-editor/constants
export { SIGNATURE_BLOCK_MARKER_ATTR, SIGNATURE_DELIMITER_HTML };

function initialContent(config?: EmailComposeConfig): ComposeContent {
  // Derive the effective body the same way EmailEditor emits edits
  // (`body: htmlToPlainText(html)`) so a `bodyHtml`-only prefill never
  // leaves raw markup in `body`. Without this, an HTML-only prefill would
  // otherwise leave `body` empty (keeping Send disabled) or, worse, ship
  // `<p>` tags verbatim to the text/plain MIME part. `bodyHtml` takes
  // precedence per EmailComposeConfig.
  const prefillHtml = config?.bodyHtml;
  const derivedBody = prefillHtml
    ? htmlToPlainText(prefillHtml)
    : config?.body || '';
  return {
    to: config?.to ?? [],
    cc: config?.cc ?? [],
    bcc: config?.bcc ?? [],
    subject: config?.subject ?? '',
    body: derivedBody,
    body_html: config?.bodyHtml,
    attachment_file_ids: [],
    signature_disposition: 'none',
  };
}

// Validation for the Send button's tooltip + disabled state. Mirrors the
// backend SendEmailParams contract (to + subject + body required). `validate`
// (not `required`) is used because the form's emptiness check treats an empty
// array as non-empty, and because these give a specific, actionable message.
// Messages must be hardcoded strings — they render directly into the DOM.
const COMPOSE_FIELDS: Record<'to' | 'subject' | 'body', FieldConfig> = {
  to: {
    validate: value =>
      Array.isArray(value) && value.length > 0
        ? undefined
        : 'Add at least one recipient',
  },
  subject: {
    validate: value =>
      typeof value === 'string' && value.trim().length > 0
        ? undefined
        : 'Add a subject',
  },
  body: {
    validate: value =>
      typeof value === 'string' && value.trim().length > 0
        ? undefined
        : 'Add a message',
  },
};

const COMPOSE_FIELD_LABELS = {
  to: 'Recipient',
  subject: 'Subject',
  body: 'Message',
};

// Derive the (userMessage, content) pair ChatResponsePanel needs from the
// chat transcript: the latest user turn and the latest assistant reply that
// followed it.
function deriveLatestTurn(messages: ComposeChatMessage[]): {
  userMessage: string | undefined;
  content: string | undefined;
} {
  let userMessage: string | undefined;
  let latestContent: string | undefined;
  for (const message of messages) {
    if (message.role === 'user') {
      userMessage = message.content;
      latestContent = undefined;
    } else {
      latestContent = message.content;
    }
  }
  return { userMessage, content: latestContent };
}

// ---------------------------------------------------------------------------
// Attachment chip
// ---------------------------------------------------------------------------

interface AttachmentChipProps {
  file: UploadedFile;
  onRemove: (fileId: number) => void;
}

function AttachmentChip({
  file,
  onRemove: handleRemoveFile,
}: AttachmentChipProps) {
  const handleRemove = useCallback(() => {
    handleRemoveFile(file.id);
  }, [handleRemoveFile, file.id]);

  return (
    <span className='inline-flex items-center gap-1 rounded-md bg-bg-secondary px-2 py-1 text-body-small text-[var(--color-text-secondary)]'>
      {file.original_filename}
      <button
        type='button'
        onClick={handleRemove}
        aria-label={`Remove ${file.original_filename}`}
        className='text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]'
      >
        <XClose className='size-3' />
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Renderer — mount once in Root.tsx
// ---------------------------------------------------------------------------

export function EmailComposeRenderer() {
  const state = useEmailComposeState();
  const { closeEmailCompose } = useEmailCompose();
  const { currentOrg } = useOrg();
  const { user } = useAuthInfo();
  const orgId = currentOrg?.id;
  const userId = user?.id;

  if (!state.isOpen || !orgId || !userId) {
    return null;
  }

  return createPortal(
    // Key on openSeq so a fresh openEmailCompose() — even while the composer is
    // already open with a different draft — remounts the panel (and the nested
    // EmailEditor) with the new config, rather than retaining stale content.
    <EmailComposePanel
      key={state.openSeq}
      orgId={orgId}
      currentUserId={userId}
      config={state.config}
      onClose={closeEmailCompose}
    />,
    document.body
  );
}

// ---------------------------------------------------------------------------
// Compose panel
// ---------------------------------------------------------------------------

export interface EmailComposePanelProps {
  orgId: number;
  currentUserId: number;
  config?: EmailComposeConfig;
  onClose: () => void;
}

// Exported (beyond the renderer) so it can be shown in Storybook with fixed
// org/user props, without booting the auth/org context the renderer resolves.
export function EmailComposePanel({
  orgId,
  currentUserId,
  config,
  onClose,
}: EmailComposePanelProps) {
  const [content, setContent] = useState<ComposeContent>(() =>
    initialContent(config)
  );
  const [attachedFiles, setAttachedFiles] = useState<UploadedFile[]>([]);
  const [isAttaching, setIsAttaching] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [chatInput, setChatInput] = useState('');
  // Remount key for EmailEditor — Plate doesn't sync body from props, so
  // applying an AI-revised draft requires a fresh editor instance.
  const [editorKey, setEditorKey] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewPayload, setPreviewPayload] =
    useState<EmailPreviewPayload | null>(null);

  // Lets Send pull the editor's latest header values synchronously — see
  // handleSend. EmailEditor debounces header propagation by 500ms.
  const editorRef = useRef<EmailEditorHandle>(null);
  // Seeding the signature is once-per-composer, not once-per-render — without
  // this the effect would re-seed every time the user cleared the body.
  // "Was a signature actually offered this session" lives on
  // content.signature_disposition ('inserted'), NOT on this ref — the ref also
  // flips true when seeding is skipped because the body was prefilled with no
  // signature at all (a canned response), which must stay 'none'.
  const hasSeededSignatureRef = useRef(false);

  const { accounts } = useConnectedAccounts({ orgId: String(orgId) });
  const { data: signatureData } = useAccountSignatures(orgId);
  const send = useSendComposedEmail(orgId, currentUserId);

  // Ref-backed view of the latest editor content, so the chat hook can read
  // it at call time (sendMessage syncs the draft onto the proposal).
  const contentRef = useRef(content);
  contentRef.current = content;
  const getContent = useCallback(() => contentRef.current, []);

  // Apply the agent's revised draft: replace the drafted fields, keep the local
  // From-account when the payload omits it, reconcile the attachment strip,
  // and remount the editor (Plate doesn't sync from props).
  const handleAgentDraft = useCallback((email: EmailContent) => {
    const attachmentIds = email.attachment_file_ids ?? [];
    setContent(prev => ({
      ...prev,
      to: email.to,
      cc: email.cc ?? [],
      bcc: email.bcc ?? [],
      subject: email.subject,
      body_html: email.body_html,
      // Plain-text fallback for validation/send; EmailEditor re-derives the
      // canonical body on the next edit.
      body:
        email.body ||
        (email.body_html ?? '')
          .replace(/<[^>]+>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim(),
      attachment_file_ids: attachmentIds,
      social_account_id: email.social_account_id ?? prev.social_account_id,
      // The stored payload's disposition survives the round-trip (the backend
      // never resurrects a user_removed signature on an AI edit — keep the
      // panel's view byte-identical to what the server holds).
      signature_disposition:
        email.signature_disposition ?? prev.signature_disposition,
    }));
    setAttachedFiles(prev =>
      prev.filter(file => attachmentIds.includes(String(file.id)))
    );
    setEditorKey(k => k + 1);
  }, []);

  const chat = useComposeProposalChat({
    orgId,
    currentUserId,
    recipientEntityId: config?.recipientEntity?.entityId,
    getContent,
    onAgentDraft: handleAgentDraft,
  });

  // Whether the user dismissed the latest chat response. Reset on the start
  // of a new turn (not on the resolved value of sendMessage) so a dismissed
  // panel reappears as soon as the next turn goes live.
  const [responseDismissed, setResponseDismissed] = useState(false);
  const handleDismissResponse = useCallback(() => {
    setResponseDismissed(true);
  }, []);
  useEffect(() => {
    if (chat.isBusy) {
      setResponseDismissed(false);
    }
  }, [chat.isBusy]);

  const latestTurn = useMemo(
    () => deriveLatestTurn(chat.messages),
    [chat.messages]
  );

  // Default the From account to the user's primary once accounts resolve.
  useEffect(() => {
    if (content.social_account_id === undefined && accounts.length > 0) {
      const primary = accounts.find(a => a.is_primary) ?? accounts[0];
      setContent(prev => ({ ...prev, social_account_id: primary.id }));
    }
  }, [accounts, content.social_account_id]);

  // Seed the signature into a fresh draft, so what the user sees is what sends.
  //
  // This cannot happen in `initialContent` — that runs synchronously at mount
  // while the signature arrives from the server. So it seeds once, on arrival,
  // and only into a draft that is still pristine: a prefilled body (canned
  // response, agent draft) or anything the user has typed is never overwritten.
  // `applyExternalContent` is the sanctioned in-place push; it does not remount
  // the editor and does not round-trip as a user edit.
  useEffect(() => {
    if (hasSeededSignatureRef.current) {
      return;
    }
    if (content.body || content.body_html) {
      // Prefilled or already typed in — leave it alone, but don't try again.
      hasSeededSignatureRef.current = true;
      // A prefilled body was never offered a signature by this effect, so it
      // must not be treated as "offered then removed" at send time — UNLESS
      // the prefill already carries the marker (e.g. an agent-drafted email
      // where the backend inserted the signature via
      // SendEmailActionHandler.validate() before the draft ever reached the
      // composer).
      if ((content.body_html ?? '').includes(SIGNATURE_BLOCK_MARKER_ATTR)) {
        setContent(prev => ({ ...prev, signature_disposition: 'inserted' }));
      }
      return;
    }
    if (editorRef.current?.isUserEditing()) {
      return;
    }
    const match = selectSignature(signatureData, content.social_account_id);
    if (!match) {
      return;
    }
    hasSeededSignatureRef.current = true;
    // Seeded bare: the empty paragraph the user types into is added by
    // parseHtmlToPlateValue, which guarantees an editable block above a
    // leading signature. Prepending `<p><br></p>` here would be a no-op —
    // that parser drops empty paragraphs by design.
    // Backend uses provider != 'outlook_mail'; the composer only ever holds
    // gmail/outlook accounts, so 'gmail' is the literal AC predicate here.
    const includeDelimiter = match.provider === 'gmail';
    const seeded = wrapSignatureBlock(match.html, includeDelimiter);
    setContent(prev => ({
      ...prev,
      body_html: seeded,
      body: htmlToPlainText(seeded),
      signature_disposition: 'inserted',
    }));
    // Headers MUST be passed through, even though this seed only changes the
    // body. `applyExternalContent` is built for applying a COMPLETE external
    // draft, so it reads an absent field as an explicit clear
    // (`content.to ?? []`) and calls setLocalTo/setLocalSubject with it.
    // Pushing `{body_html}` alone therefore wiped the prefilled recipient and
    // subject out of the editor — and because the method also arms
    // `suppressHeaderSaveRef`, the wipe never propagated back, so the panel's
    // own `content.to` still looked correct while the field rendered empty.
    //
    // Read via contentRef (assigned every render) rather than `content`, so
    // the effect does not take a dependency on every header keystroke just to
    // echo values it never changes.
    const headers = contentRef.current;
    editorRef.current?.applyExternalContent({
      to: headers.to,
      cc: headers.cc,
      bcc: headers.bcc,
      subject: headers.subject,
      body_html: seeded,
    });
  }, [
    signatureData,
    content.social_account_id,
    content.body,
    content.body_html,
  ]);

  const handleContentChange = useCallback(
    (updates: {
      to?: string[];
      cc?: string[];
      bcc?: string[];
      subject?: string;
      body_html?: string;
      body?: string;
      social_account_id?: number;
    }) => {
      setContent(prev => {
        // EmailEditor emits two distinct partial shapes: a header snapshot that
        // always carries `to` (with `cc`/`bcc` present as arrays, or `undefined`
        // when the field is empty), and a body-only snapshot that omits the
        // header fields entirely. Discriminate on `to`: on a header snapshot an
        // `undefined` cc/bcc is an *explicit clear* (map to []), so removing the
        // last chip actually drops the address; on a body snapshot the header
        // fields are absent and must be preserved. Either way cc/bcc stay arrays
        // so reads like `content.cc.length` never throw.
        const isHeaderSnapshot = updates.to !== undefined;
        // reconcileSignatureDisposition flips 'inserted' → 'user_removed' the
        // moment an edit leaves body_html without the marker block (the ✕ on
        // the signature block, select+delete, or a modal-triggered removal all
        // arrive here as ordinary content changes). Same reconciliation the
        // Action Center editor runs — and the backend's UI-edit rule.
        return reconcileSignatureDisposition({
          ...prev,
          ...updates,
          to: updates.to ?? prev.to,
          cc: isHeaderSnapshot ? (updates.cc ?? []) : prev.cc,
          bcc: isHeaderSnapshot ? (updates.bcc ?? []) : prev.bcc,
        });
      });
    },
    []
  );

  const handleFromChange = useCallback((accountId: number) => {
    setContent(prev => ({ ...prev, social_account_id: accountId }));
  }, []);

  const handleAttachFiles = useCallback(
    async (files: File[]) => {
      setIsAttaching(true);
      try {
        const existingTotalBytes = attachedFiles.reduce(
          (sum, file) => sum + file.file_size,
          0
        );
        const { accepted, rejectedCount: rejectedForSize } =
          filterFilesByAttachmentCap(
            files,
            existingTotalBytes,
            MAX_EMAIL_ATTACHMENT_TOTAL_BYTES
          );

        const results = await Promise.allSettled(
          accepted.map(file => fileService.uploadFile(orgId, file))
        );
        const uploaded: UploadedFile[] = [];
        let failures = 0;
        for (const result of results) {
          if (result.status === 'fulfilled') {
            uploaded.push(result.value);
          } else {
            failures += 1;
          }
        }
        if (uploaded.length > 0) {
          setAttachedFiles(prev => [...prev, ...uploaded]);
          setContent(prev => ({
            ...prev,
            attachment_file_ids: [
              ...prev.attachment_file_ids,
              ...uploaded.map(file => String(file.id)),
            ],
          }));
        }
        if (failures > 0) {
          toast.error(
            `${failures} file${failures > 1 ? 's' : ''} couldn't be attached.`
          );
        }
        if (rejectedForSize > 0) {
          toast.error(
            `Email attachments are limited to ${MAX_EMAIL_ATTACHMENT_TOTAL_MB} MB total.`
          );
        }
      } finally {
        setIsAttaching(false);
      }
    },
    [orgId, attachedFiles]
  );

  const handleRemoveFile = useCallback(
    (fileId: number) => {
      setAttachedFiles(prev => prev.filter(file => file.id !== fileId));
      setContent(prev => ({
        ...prev,
        attachment_file_ids: prev.attachment_file_ids.filter(
          id => id !== String(fileId)
        ),
      }));
      // Best-effort cleanup of the uploaded blob; ignore failures.
      void fileService.deleteFile(orgId, fileId).catch(() => {});
    },
    [orgId]
  );

  const isDirty =
    content.to.length > 0 ||
    content.cc.length > 0 ||
    content.bcc.length > 0 ||
    content.subject.trim().length > 0 ||
    content.body.trim().length > 0 ||
    content.attachment_file_ids.length > 0;

  // An attachment upload or a send in flight owns async work that mutates
  // message/attachment state after it resolves; closing mid-flight would send
  // after discard or orphan half-uploaded blobs. Block every user-initiated
  // close path while busy (the header X, footer Cancel, and the discard modal's
  // own trigger all route through guardedClose). handleSend closes the panel
  // itself on success, so a successful send still dismisses normally. An
  // in-flight AI chat turn does NOT block closing — discard cancels it.
  const isBusy = isSending || isAttaching;

  const guardedClose = useCallback(() => {
    if (isBusy) {
      return;
    }
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    // Even a clean-looking draft may have a pending proposal from a chat turn
    // that was later cleared — discard it silently.
    void chat.discardProposal();
    onClose();
  }, [isBusy, isDirty, chat, onClose]);

  const handleDiscard = useCallback(() => {
    setShowDiscardConfirm(false);
    // Delete any uploaded attachment blobs so a discarded draft doesn't leave
    // orphaned files behind (uploads happen eagerly on attach). Best-effort;
    // ignore failures — the panel is closing regardless.
    for (const file of attachedFiles) {
      void fileService.deleteFile(orgId, file.id).catch(() => {});
    }
    // Stop any in-flight agent turn and silently delete the pending proposal
    // backing this draft.
    void chat.discardProposal();
    onClose();
  }, [attachedFiles, orgId, chat, onClose]);

  const handleKeepEditing = useCallback(() => {
    setShowDiscardConfirm(false);
  }, []);

  // AI-edit chat: one discussion turn with the proposal agent. The hook syncs
  // the current draft onto the pending proposal, runs the turn, and applies
  // the revised draft via onAgentDraft when it lands.
  const handleChatSend = useCallback(async () => {
    // Mirror handleSend's guards: don't start a turn while an upload is in
    // flight (its id isn't in content yet, so the turn would snapshot a draft
    // missing the attachment) or while a direct send is running (the turn
    // could outlive the panel).
    if (isAttaching || isSending || chat.isBusy) {
      return;
    }
    // Header edits reach content state through EmailEditor's 500ms debounce, so
    // an immediate chat submit could otherwise start the turn from stale
    // recipients/subject. Flush the pending header edit and merge it into the
    // ref the chat hook reads synchronously — setContent from flushHeaders only
    // lands on the next render, too late for this call.
    const header = editorRef.current?.flushHeaders();
    if (header) {
      contentRef.current = {
        ...contentRef.current,
        to: header.to,
        cc: header.cc ?? [],
        bcc: header.bcc ?? [],
        subject: header.subject,
      };
    }
    const started = await chat.sendMessage(chatInput);
    if (started) {
      setChatInput('');
    }
  }, [chat, chatInput, isAttaching, isSending]);

  const handleChatSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      void handleChatSend();
    },
    [handleChatSend]
  );

  // Values the Send button validates against (see COMPOSE_FIELDS). A fresh
  // reference on each change lets FormProvider clear any stale submit error.
  const formValues = useMemo(
    () => ({
      to: content.to,
      subject: content.subject,
      body: content.body,
    }),
    [content.to, content.subject, content.body]
  );

  // Preview composes through the same send path, so it needs the same three
  // fields Send validates. Derived from COMPOSE_FIELDS rather than restating
  // the rule — one source of truth, and its messages are already specific.
  const previewBlockedReason = useMemo(
    () =>
      (['to', 'subject', 'body'] as const)
        .map(key => COMPOSE_FIELDS[key].validate?.(formValues[key]))
        .find((message): message is string => Boolean(message)),
    [formValues]
  );

  // Snapshots the flushed headers at click time, matching Send's semantics
  // (see handleSend below). Intentionally NOT wired to live compose state —
  // the modal is read-only and won't reflect edits made after it opens.
  const buildPreviewPayload = useCallback((): EmailPreviewPayload => {
    const header = editorRef.current?.flushHeaders();
    return {
      to: header?.to ?? content.to,
      cc: optionalRecipients(header ? header.cc : content.cc),
      bcc: optionalRecipients(header ? header.bcc : content.bcc),
      subject: (header?.subject ?? content.subject).trim(),
      body: content.body,
      body_html: content.body_html,
      social_account_id: content.social_account_id,
      signature_disposition: content.signature_disposition,
    };
  }, [
    content.to,
    content.cc,
    content.bcc,
    content.subject,
    content.body,
    content.body_html,
    content.social_account_id,
    content.signature_disposition,
  ]);

  const handlePreview = useCallback(() => {
    setPreviewPayload(buildPreviewPayload());
    setIsPreviewOpen(true);
  }, [buildPreviewPayload]);

  // Keep the open modal's snapshot in sync with signature changes made FROM
  // the modal (remove / add back): body and disposition are the only fields
  // those actions touch. Headers deliberately stay the click-time snapshot —
  // the modal is otherwise read-only.
  useEffect(() => {
    if (!isPreviewOpen) {
      return;
    }
    setPreviewPayload(prev =>
      prev
        ? {
            ...prev,
            body: content.body,
            body_html: content.body_html,
            signature_disposition: content.signature_disposition,
          }
        : prev
    );
  }, [
    isPreviewOpen,
    content.body,
    content.body_html,
    content.signature_disposition,
  ]);

  // Remove the signature from the draft via the live editor — the identical
  // Plate transform as the block's own remove button, so the change propagates
  // through handleContentChange and reconciles to 'user_removed'. When no
  // in-draft block exists (a draft whose signature the server appends at send
  // time), there is nothing to delete from the editor, so record the removal
  // directly — 'user_removed' is honoured unconditionally at send.
  const handleRemoveSignatureFromPreview = useCallback(() => {
    const removed = editorRef.current?.removeSignature() ?? false;
    if (!removed) {
      setContent(prev => ({ ...prev, signature_disposition: 'user_removed' }));
    }
  }, []);

  // Re-insert the signature after a removal: append a fresh marker block and
  // push it into the live editor the same way the seed effect does. When no
  // signature resolves client-side, fall back to disposition 'none' so the
  // send path appends the server-resolved signature instead.
  const handleAddSignatureBack = useCallback(() => {
    const { current } = contentRef;
    const match = selectSignature(signatureData, current.social_account_id);
    if (!match) {
      setContent(prev => ({ ...prev, signature_disposition: 'none' }));
      return;
    }
    const includeDelimiter = match.provider === 'gmail';
    const { body_html: appendedHtml, body: appendedBody } =
      appendSignatureBlock(current, match.html, includeDelimiter);
    setContent(prev => ({
      ...prev,
      body_html: appendedHtml,
      body: appendedBody,
      signature_disposition: 'inserted',
    }));
    editorRef.current?.applyExternalContent({
      to: current.to,
      cc: current.cc,
      bcc: current.bcc,
      subject: current.subject,
      body_html: appendedHtml,
    });
  }, [signatureData]);

  const handleSend = useCallback(async () => {
    // SubmitButton only fires onClick when the form is valid; guard re-entry,
    // block while an attachment upload is still in flight (its id isn't in
    // content yet, so sending now would silently drop the file), and block
    // while an agent turn is rewriting the draft.
    if (isSending || isAttaching || chat.isBusy) {
      return;
    }
    // Header edits reach us through EmailEditor's 500ms debounce, so an
    // immediate Send could otherwise dispatch stale recipients/subject. Pull the
    // editor's current header values synchronously and send those.
    const header = editorRef.current?.flushHeaders();
    const to = header?.to ?? content.to;
    const cc = optionalRecipients(header ? header.cc : content.cc);
    const bcc = optionalRecipients(header ? header.bcc : content.bcc);
    const subject = (header?.subject ?? content.subject).trim();

    setIsSending(true);
    setError(null);
    try {
      await send({
        to,
        subject,
        body: content.body,
        cc,
        bcc,
        body_html: content.body_html,
        attachment_file_ids:
          content.attachment_file_ids.length > 0
            ? content.attachment_file_ids
            : undefined,
        social_account_id: content.social_account_id,
        // The tracked tri-state: 'none' matches the backend default (append if
        // resolvable), 'inserted' skips the append and re-mints the block,
        // 'user_removed' is the durable never-re-add signal.
        signature_disposition: content.signature_disposition,
        recipientEntityId: config?.recipientEntity?.entityId,
        // When the AI chat created a pending proposal for this draft, Send
        // flushes final content onto it and approves that same proposal.
        proposalId: chat.proposalId ?? undefined,
        actionId: chat.actionId ?? undefined,
      });
      // Send approved this draft's pending proposal — release it so the chat
      // hook's unmount cleanup doesn't delete the now-approved proposal.
      chat.releaseProposal();
      toast.success('Email sent');
      onClose();
    } catch (e) {
      const message = "We couldn't send this email.";
      setError(message);
      toast.error(message);
      console.error(e);
    } finally {
      setIsSending(false);
    }
  }, [
    isSending,
    isAttaching,
    send,
    content.to,
    content.cc,
    content.bcc,
    content.subject,
    content.body,
    content.body_html,
    content.attachment_file_ids,
    content.social_account_id,
    content.signature_disposition,
    onClose,
    config?.recipientEntity,
    chat,
  ]);

  // Composer is non-editable while an AI chat turn is rewriting the draft —
  // shared by the EmailEditor's editable prop and the Preview button gating.
  const editable = !chat.isBusy;

  const attachmentStrip = useMemo(() => {
    if (attachedFiles.length === 0) {
      return null;
    }
    return (
      <div className='flex flex-wrap gap-2 px-4 pb-2'>
        {attachedFiles.map(file => (
          <AttachmentChip
            key={file.id}
            file={file}
            onRemove={handleRemoveFile}
          />
        ))}
      </div>
    );
  }, [attachedFiles, handleRemoveFile]);

  return (
    <ComposeShell dataInstance='email'>
      <FormProvider
        fields={COMPOSE_FIELDS}
        values={formValues}
        fieldLabels={COMPOSE_FIELD_LABELS}
      >
        <div className='flex h-full flex-col'>
          {/* Header */}
          <div className='flex shrink-0 items-center justify-between border-b border-[var(--color-border-secondary)] px-4 py-3'>
            <h2 className='text-body font-semibold'>New email</h2>
            <button
              type='button'
              onClick={guardedClose}
              disabled={isBusy}
              aria-label='Close'
              className='text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] disabled:cursor-not-allowed disabled:opacity-50'
            >
              <XClose className='size-4' />
            </button>
          </div>

          {/* Body — recipients + subject + rich body live inside EmailEditor */}
          <div className='min-h-0 flex-1 overflow-y-auto'>
            <EmailEditor
              key={editorKey}
              ref={editorRef}
              to={content.to}
              cc={content.cc}
              bcc={content.bcc}
              subject={content.subject}
              bodyHtml={content.body_html}
              body={content.body}
              onContentChange={handleContentChange}
              editable={editable}
              connectedAccounts={accounts}
              selectedAccountId={content.social_account_id}
              onFromChange={handleFromChange}
              orgId={orgId}
              onAttachFile={handleAttachFiles}
              isAttaching={isAttaching}
              // No onPreview here: in the composer, Preview lives beside Send in
              // the footer where a pre-send check belongs. EmailEditor renders
              // its own Preview pill only where there is no action bar to host
              // it (proposal / AI-action cards).
              embedded
              initialHeaderEditing
              linkRecipientsToRecords
            />
            {attachmentStrip}
          </div>

          {/* AI-edit chat */}
          {!responseDismissed && (
            <ChatResponsePanel
              orgId={orgId}
              userMessage={latestTurn.userMessage}
              content={latestTurn.content}
              isLive={chat.isBusy}
              onDismiss={handleDismissResponse}
            />
          )}
          <div className='shrink-0 border-t border-[var(--color-border-secondary)] px-4 py-2'>
            <ConversationInput
              value={chatInput}
              onChange={setChatInput}
              onSubmit={handleChatSubmit}
              placeholder='Ask AI to edit this email'
              variant='simple'
              sticky={false}
              orgId={orgId}
              enableVoice={false}
              loading={chat.isBusy || isAttaching || isSending}
              isProcessing={chat.isBusy}
              error={chat.error ?? null}
              compactUntilWrap
            />
          </div>

          {/* Footer */}
          {error && (
            <div className='shrink-0 px-4 pb-1 text-body-small text-[var(--color-text-error-primary)]'>
              {error}
            </div>
          )}
          <div
            className={cx(
              'flex shrink-0 items-center justify-end gap-2',
              'border-t border-[var(--color-border-secondary)] px-4 py-3'
            )}
          >
            <Button
              color='secondary'
              onClick={guardedClose}
              isDisabled={isBusy}
            >
              Cancel
            </Button>
            <Button
              color='secondary'
              onClick={handlePreview}
              isDisabled={isBusy || previewBlockedReason !== undefined}
              title={
                previewBlockedReason ?? 'Preview this email as it will send'
              }
            >
              Preview
            </Button>
            <SubmitButton
              color='primary'
              onClick={handleSend}
              isDisabled={isSending || isAttaching || chat.isBusy}
              errorSummaryTitle="This email isn't ready to send yet:"
            >
              {isSending ? 'Sending…' : 'Send'}
            </SubmitButton>
          </div>
        </div>
      </FormProvider>
      <ConfirmationDialog
        isOpen={showDiscardConfirm}
        variant='destructive'
        title='Discard this email?'
        description='Your draft will be lost. This cannot be undone.'
        confirmLabel='Discard'
        cancelLabel='Keep editing'
        instance='discard-email'
        onConfirm={handleDiscard}
        onCancel={handleKeepEditing}
      />
      {previewPayload && orgId !== undefined && (
        <EmailPreviewModal
          isOpen={isPreviewOpen}
          onOpenChange={setIsPreviewOpen}
          orgId={orgId}
          payload={previewPayload}
          onRemoveSignature={handleRemoveSignatureFromPreview}
          onAddSignatureBack={handleAddSignatureBack}
        />
      )}
    </ComposeShell>
  );
}
